import React, { useState, useRef, useEffect } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronDown,
  Check, Loader2, ArrowLeft,
  FileText, AlertCircle, Zap,
  Stethoscope, Activity, FlaskConical, HeartPulse, Wind, Thermometer, Scale, Heart, Eye, Play,
  Image as ImageIcon, Video
} from 'lucide-react';
import { useCreateCase, useUpdateCase } from '../hooks/use-cases';
import { useQueryClient } from '@tanstack/react-query';
import InlineMediaUploader, { SectionMediaGrid } from './SectionMediaUploader';
import MediaPreviewModal from './MediaPreviewModal';
import QuickDiscussionComposer from './QuickDiscussionComposer';
import { formatMediaUrl, presignStagedPreview } from '../api/media-api';
import { useAuth } from '../../../contexts/AuthContext';
import { draftStore } from '../../../hooks/useDraftStore';

/**
 * Strips the media state that cannot survive a page load, before the draft is
 * written to localStorage.
 *
 * Three things break across a reload:
 *   - a `blob:` URL belongs to the document that created it, so a restored
 *     draft points at objects the browser has already discarded
 *   - the `file` handle does not survive JSON at all, so an interrupted upload
 *     can never be resumed
 *   - an item saved mid-upload comes back with `uploading: true` and nothing
 *     left running to clear it, which blocks Publish permanently
 *
 * Only finished uploads are worth keeping: their bytes are already in the
 * bucket and the key is all the server needs on submit.
 */
const draftSafeMedia = (media = []) =>
  (media || [])
    .filter(
      (m) =>
        !m.uploading &&
        !m.failed &&
        typeof m.url === 'string' &&
        !m.url.startsWith('blob:')
    )
    .map(({ file, previewUrl, uploading, progress, failed, ...rest }) => rest);

// ─── Interactive Preview Media Gallery ──────────────────────────────────────
const PreviewMediaGallery = ({ items = [] }) => {
  // Track WHICH item is open, not a copy of it. A staged file's thumbnail
  // arrives asynchronously, so a snapshot taken on click keeps whatever the
  // item looked like at that moment — which is how the lightbox ended up
  // showing a broken image while the tile behind it was fine.
  const [selectedKey, setSelectedKey] = useState(null);
  const selectedMedia = selectedKey
    ? items.find((m) => m.url === selectedKey) ?? null
    : null;

  if (!items || items.length === 0) return null;

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
        {items.map((m, idx) => {
          const isVideo = m.type === 'VIDEO' || m.type === 'video' || m.mimeType?.startsWith('video') || /\.(mp4|webm|mov|m4v)$/i.test(m.url || '');

          // Prefer the local preview, exactly as SectionMediaGrid does. As soon
          // as a file finishes uploading, `url` is swapped for its storage key,
          // which is not a fetchable address — rendering it produced the blank
          // tiles. Media loaded from the API has no previewUrl and is already
          // signed, so it falls through to `url`.
          // A draft restored after a page load keeps only the storage key, whose
          // blob preview died with the document that made it. Leave the tile
          // empty rather than requesting an address that cannot resolve.
          const resolved = formatMediaUrl(m.previewUrl || m.url);
          const src = /^(https?:|blob:|data:)/.test(resolved || '') ? resolved : undefined;

          return (
            <div
              key={idx}
              onClick={() => setSelectedKey(m.url)}
              className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer border border-gray-200 bg-gray-900 shadow-sm hover:border-indigo-500 hover:shadow-md transition-all"
            >
              {!src ? (
                /* Restored from a saved draft: the file is uploaded and its key
                   is held, but the local preview died with the page that made
                   it and a staged object has no signed URL. Name the file so it
                   reads as attached rather than lost. */
                <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 bg-gray-800 text-gray-300 px-2 text-center">
                  {isVideo ? <Video size={20} /> : <ImageIcon size={20} />}
                  <span className="text-[10px] font-medium leading-tight break-all line-clamp-2">
                    {m.filename || (isVideo ? 'Video' : 'Image')}
                  </span>
                  <span className="text-[9px] text-gray-400">Attached</span>
                </div>
              ) : isVideo ? (
                <>
                  <video
                    src={src}
                    muted
                    preload="metadata"
                    playsInline
                    className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 flex items-center justify-center transition-colors">
                    <div className="w-10 h-10 rounded-full bg-white/90 group-hover:bg-white text-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play size={18} className="ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* No remote placeholder on error. via.placeholder.com is a
                      third-party host that is unreachable on some networks, so
                      the fallback timed out and buried the real failure under
                      repeated ERR_CONNECTION_TIMED_OUT noise. A tile that
                      cannot load now stays on its own dark background. */}
                  <img
                    src={src}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 flex items-center justify-center transition-colors">
                    <div className="w-9 h-9 rounded-full bg-white/90 opacity-0 group-hover:opacity-100 flex items-center justify-center text-gray-900 shadow transition-all transform translate-y-1 group-hover:translate-y-0">
                      <Eye size={16} />
                    </div>
                  </div>
                </>
              )}

              {/* Same progress overlay as the form grid. Without it the preview
                  showed a finished-looking tile while the transfer was still
                  running, and Publish refused with nothing to point at. */}
              {m.uploading && (
                <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center text-white">
                  <div className="w-8 h-8 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  <span className="mt-1.5 text-[11px] font-semibold">{m.progress ?? 0}%</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedMedia && (
        <MediaPreviewModal
          mediaItem={selectedMedia}
          onClose={() => setSelectedKey(null)}
        />
      )}
    </>
  );
};

// ── Step Config ─────────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, key: 'patient', label: 'Patient Info' },
  { id: 2, key: 'complaint', label: 'Chief Complaint' },
  { id: 3, key: 'findings', label: 'Clinical Findings' },
  { id: 4, key: 'assessment', label: 'Diagnostics' },
  { id: 5, key: 'treatment', label: 'Diagnosis & Outcome' },
];

// ─── Dropdown Options ─────────────────────────────────────────────────────────
const BCS_OPTIONS = [
  '—',
  '1 – Emaciated',
  '2 – Very thin',
  '3 – Thin',
  '4 – Underweight',
  '5 – Ideal',
  '6 – Overweight',
  '7 – Heavy',
  '8 – Obese',
  '9 – Morbidly obese',
];
const PAIN_OPTIONS = ['0 – None', '1 – Mild', '2 – Moderate', '3 – Severe', '4 – Excruciating'];
const CRT_OPTIONS = ['—', '< 1 sec', '1–2 sec (Normal)', '2–3 sec', '3–4 sec', '> 4 sec'];
const MM_OPTIONS = [
  '—',
  'Pink (Normal)',
  'Pale pink',
  'White/Pale',
  'Muddy/Tacky',
  'Brick red',
  'Cyanotic (Blue)',
  'Icteric (Yellow)',
];
const STERILIZATION_OPTIONS = ['Intact', 'Neutered', 'Spayed', 'Unknown'];
const AGE_UNITS = ['days', 'weeks', 'months', 'years'];
const WEIGHT_UNITS = ['kg', 'lbs'];

const DOG_BREEDS = [
  'Labrador Retriever',
  'German Shepherd',
  'Golden Retriever',
  'French Bulldog',
  'Beagle',
  'Poodle',
  'Rottweiler',
  'Shih Tzu',
  'Boxer',
  'Dachshund',
  'Other'
];

const CAT_BREEDS = [
  'Persian',
  'Maine Coon',
  'Siamese',
  'British Shorthair',
  'Ragdoll',
  'Bengal',
  'Sphynx',
  'Abyssinian',
  'American Shorthair',
  'Scottish Fold',
  'Other'
];

const COW_BREEDS = [
  'Gir',
  'Sahiwal',
  'Red Sindhi',
  'Tharparkar',
  'Kankrej',
  'Ongole',
  'Hariana',
  'Holstein Friesian (HF)',
  'Jersey',
  'Rathi',
  'Crossbred',
  'Other'
];

const HORSE_BREEDS = [
  'Marwari',
  'Kathiawari',
  'Thoroughbred',
  'Arabian',
  'Zanskari',
  'Spiti',
  'Bhutia',
  'Manipuri Pony',
  'Kachchhi-Sindhi',
  'Warmblood',
  'Other'
];

// ─── Style Constants ──────────────────────────────────────────────────────────
const fieldCls = 'w-full px-4 py-3 text-sm bg-[#F5F6F8] rounded-xl border border-transparent outline-none focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all placeholder:text-gray-400';
const labelCls = 'block text-sm font-semibold text-gray-800 mb-1.5';
const subLabelCls = 'text-xs text-gray-400 font-normal';

// ─── Character Limits ─────────────────────────────────────────────────────────
const MAX_TITLE_LENGTH = 255;
const MAX_BREED_LENGTH = 50;
const MAX_FIELD_LENGTH = 3000;

// ─── Custom Select (matches registration form style) ─────────────────────────
const CustomSelect = ({ options, value, onChange, placeholder, dropUp }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3.5 py-3 bg-[#F5F6F8] rounded-xl text-sm transition-all text-left border ${isOpen ? 'bg-white border-indigo-500 ring-2 ring-indigo-100 shadow-sm' : 'border-transparent hover:border-gray-300 hover:bg-[#eceef2]'
          }`}
      >
        <span className={value ? 'text-gray-900 font-medium' : 'text-gray-400'}>
          {value || placeholder || 'Select'}
        </span>
        <ChevronDown size={15} className={`text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={`absolute z-50 w-full bg-white border border-gray-200 rounded-xl shadow-[0_12px_30px_-6px_rgba(0,0,0,0.14)] p-1.5 max-h-52 overflow-auto ${
          dropUp ? 'bottom-full mb-1.5 left-0' : 'top-full mt-1.5 left-0'
        }`}>
          {options.map((option) => {
            const isSelected = value === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => { onChange(option); setIsOpen(false); }}
                className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg text-sm transition-colors ${isSelected ? 'bg-indigo-50 text-indigo-900 font-semibold' : 'text-gray-800 hover:bg-[#F5F6F8] font-medium'
                  }`}
              >
                <span>{option}</span>
                {isSelected && <Check size={15} className="text-indigo-600" strokeWidth={2.5} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Pill Button ─────────────────────────────────────────────────────────────
const PillBtn = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all ${active
      ? 'border-indigo-500 text-indigo-600 bg-indigo-50'
      : 'border-gray-300 text-gray-600 bg-white hover:border-gray-400'
      }`}
  >
    {label}
  </button>
);

// ─── Horizontal Stepper (Figma Exact) ─────────────────────────────────────────
const HorizontalStepper = ({ current }) => (
  <div className="w-full mb-6 pb-5 border-b border-gray-100/80">
    <div className="flex items-center justify-between w-full px-0">
      {STEPS.map((s, idx) => {
        const isDone = current > s.id;
        const isActive = current === s.id;

        return (
          <React.Fragment key={s.id}>
            {/* Step Node */}
            <div className="flex flex-col items-center gap-1 relative z-10 min-w-0">
              <div
                className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-[11px] sm:text-xs font-bold transition-all shrink-0 ${isDone || isActive
                  ? 'bg-[#6C63FF] text-white shadow-sm'
                  : 'bg-gray-100 text-gray-400'
                  }`}
              >
                {isDone ? <Check size={13} strokeWidth={2.5} /> : s.id}
              </div>
              {/* Label — always visible, wraps on mobile */}
              <span
                className={`text-[9px] sm:text-[11px] sm:whitespace-nowrap text-center leading-tight max-w-[44px] sm:max-w-none transition-colors ${isActive
                  ? 'text-[#6C63FF] font-bold'
                  : isDone
                    ? 'text-gray-600 font-medium'
                    : 'text-gray-400 font-medium'
                  }`}
              >
                {s.label}
              </span>
            </div>

            {/* Connecting Line */}
            {idx < STEPS.length - 1 && (
              <div
                className={`flex-1 h-[2px] mx-1 sm:mx-2 -mt-4 sm:-mt-5 transition-colors ${current > s.id ? 'bg-[#6C63FF]' : 'bg-gray-200'
                  }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  </div>
);


// ─── Editable Breed Combobox (Single Box with Suggestions & Direct Typing) ────
const BreedCombobox = ({ value, onChange, suggestions = [], placeholder, maxLength }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = suggestions.filter((s) =>
    s.toLowerCase().includes((value || '').toLowerCase())
  );

  return (
    <div className="relative" ref={ref}>
      <input
        type="text"
        className={fieldCls}
        value={value || ''}
        onChange={(e) => {
          onChange(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        maxLength={maxLength}
        placeholder={placeholder || 'Type or select breed...'}
      />

      {isOpen && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-[0_12px_30px_-6px_rgba(0,0,0,0.14)] p-1.5 max-h-52 overflow-auto">
          {filtered.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                onChange(opt);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3.5 py-2.5 text-xs sm:text-sm rounded-lg transition-colors flex items-center justify-between ${value === opt ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-700 hover:bg-gray-100'
                }`}
            >
              <span>{opt}</span>
              {value === opt && <Check size={14} className="text-indigo-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── STEP 1: Patient Info ─────────────────────────────────────────────────────
function Step1({ form, onChange }) {
  const isDog = form.species === 'Dog';
  const isCat = form.species === 'Cat';
  const isCow = form.species === 'Cow';
  const isHorse = form.species === 'Horse';
  const isOtherSpecies = form.species === 'Other' || (!isDog && !isCat && !isCow && !isHorse && !!form.species);
  const currentBreeds = isDog
    ? DOG_BREEDS
    : isCat
    ? CAT_BREEDS
    : isCow
    ? COW_BREEDS
    : isHorse
    ? HORSE_BREEDS
    : [];

  return (
    <div className="space-y-5">
      {/* Case Title */}
      <div>
        <label className={labelCls}>Case Title <span className="text-red-500">*</span></label>
        <input
          className={fieldCls}
          value={form.title}
          onChange={(e) => onChange('title', e.target.value)}
          placeholder="e.g. Acute Vomiting in a Dog: Contrast CT-Guided Diagnosis..."
          maxLength={MAX_TITLE_LENGTH}
        />
        <FieldCounter current={form.title.length} max={MAX_TITLE_LENGTH} />
      </div>

      {/* Species */}
      <div>
        <label className={labelCls}>Species <span className="text-red-500">*</span></label>
        <div className="flex flex-wrap gap-2">
          <PillBtn label="🐕 Dog" active={isDog} onClick={() => { onChange('species', 'Dog'); onChange('customSpecies', ''); }} />
          <PillBtn label="🐈 Cat" active={isCat} onClick={() => { onChange('species', 'Cat'); onChange('customSpecies', ''); }} />
          <PillBtn label="🐄 Cow" active={isCow} onClick={() => { onChange('species', 'Cow'); onChange('customSpecies', ''); }} />
          <PillBtn label="🐎 Horse" active={isHorse} onClick={() => { onChange('species', 'Horse'); onChange('customSpecies', ''); }} />
          <PillBtn label="🐾 Other" active={isOtherSpecies} onClick={() => onChange('species', 'Other')} />
        </div>

        {/* Custom Species Input when 'Other' is selected */}
        {isOtherSpecies && (
          <div className="mt-3">
            <label className={labelCls}>Specify Species <span className="text-red-500">*</span></label>
            <input
              className={fieldCls}
              value={form.customSpecies || (form.species !== 'Other' ? form.species : '')}
              onChange={(e) => {
                onChange('customSpecies', e.target.value);
                onChange('species', e.target.value || 'Other');
              }}
              placeholder="e.g. Rabbit, Goat, Sheep, Buffalo, Camel, Parrot..."
              maxLength={MAX_BREED_LENGTH}
            />
          </div>
        )}
      </div>

      {/* Breed — Single unified editable combobox */}
      <div>
        <label className={labelCls}>Breed</label>
        <BreedCombobox
          value={form.breed}
          onChange={(v) => onChange('breed', v)}
          suggestions={currentBreeds}
          maxLength={MAX_BREED_LENGTH}
          placeholder={
            isDog ? "e.g. Labrador, German Shepherd..." :
            isCat ? "e.g. Persian, Siamese..." :
            isCow ? "e.g. Gir, Sahiwal, HF..." :
            isHorse ? "e.g. Marwari, Kathiawari, Thoroughbred..." :
            "Type breed name..."
          }
        />
      </div>

      {/* Age + Weight — one row, each with inline unit selector */}
      <div>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className={labelCls}>Age <span className="text-red-500">*</span></label>
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                className={`${fieldCls} flex-1 min-w-0`}
                value={form.ageValue}
                onChange={(e) => onChange('ageValue', e.target.value)}
                placeholder="e.g. 5"
              />
              <div className="w-20 shrink-0">
                <CustomSelect
                  options={AGE_UNITS}
                  value={form.ageUnit}
                  onChange={(v) => onChange('ageUnit', v)}
                  placeholder="Unit"
                />
              </div>
            </div>
          </div>
          <div className="flex-1">
            <label className={labelCls}>Weight <span className={subLabelCls}>(optional)</span></label>
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                step="0.1"
                className={`${fieldCls} flex-1 min-w-0`}
                value={form.weightValue}
                onChange={(e) => onChange('weightValue', e.target.value)}
                placeholder="0.0"
              />
              <div className="w-16 shrink-0">
                <CustomSelect
                  options={WEIGHT_UNITS}
                  value={form.weightUnit}
                  onChange={(v) => onChange('weightUnit', v)}
                  placeholder="kg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sex + Sterilization */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
        <div>
          <label className={labelCls}>Sex <span className="text-red-500">*</span></label>
          <div className="flex gap-2">
            <PillBtn label="Male" active={form.sex === 'Male'} onClick={() => onChange('sex', 'Male')} />
            <PillBtn label="Female" active={form.sex === 'Female'} onClick={() => onChange('sex', 'Female')} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Sterilization</label>
          <CustomSelect
            options={STERILIZATION_OPTIONS}
            value={form.sterilization}
            onChange={(v) => onChange('sterilization', v)}
            placeholder="Select"
            dropUp={true}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Field Counter ────────────────────────────────────────────────────────────
// Shows current/max characters below a field. Turns amber at 90%, red at 100%.
const FieldCounter = ({ current, max }) => (
  <p className={`text-right text-[10px] mt-1 font-medium transition-colors ${
    current >= max          ? 'text-red-500'
    : current >= max * 0.9  ? 'text-amber-500'
    : 'text-gray-400'
  }`}>
    {current}/{max}
  </p>
);

// ─── Auto-Growing Textarea Component ──────────────────────────────────────────
const AutoTextarea = ({ className, value, onChange, placeholder, rows = 4, name, id, disabled, readOnly, maxLength }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.max(textareaRef.current.scrollHeight, rows * 24)}px`;
    }
  }, [value, rows]);

  return (
    <textarea
      ref={textareaRef}
      className={`${className || fieldCls} overflow-hidden resize-none transition-all duration-150`}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      name={name}
      id={id}
      disabled={disabled}
      readOnly={readOnly}
      maxLength={maxLength}
    />
  );
};

// ─── STEP 2: Chief Complaint ──────────────────────────────────────────────────
const Step2 = ({ form, onChange }) => (
  <div className="space-y-5">
    {/* Chief Complaint */}
    <div>
      <label className={labelCls}>Chief Complaint <span className="text-red-500">*</span></label>
      <p className="text-xs text-gray-400 mb-2">
        Describe the primary reason the patient was presented. Include duration, onset, and any owner observations.
      </p>
      <AutoTextarea
        rows={5}
        value={form.chiefComplaint}
        onChange={(e) => onChange('chiefComplaint', e.target.value)}
        placeholder="e.g. Owner reports 3-week history of intermittent vomiting, progressive weight loss of approximately 2 kg, and variable appetite..."
        maxLength={MAX_FIELD_LENGTH}
      />
      <FieldCounter current={form.chiefComplaint.length} max={MAX_FIELD_LENGTH} />
    </div>

    {/* History */}
    <div>
      <label className={labelCls}>History <span className={subLabelCls}>(optional)</span></label>
      <AutoTextarea
        rows={4}
        value={form.history}
        onChange={(e) => onChange('history', e.target.value)}
        placeholder="e.g. Vaccinated, dewormed. No prior illnesses. Owner reports lethargy for 3 days, reduced appetite. No recent dietary change or travel..."
        maxLength={MAX_FIELD_LENGTH}
      />
      <FieldCounter current={form.history.length} max={MAX_FIELD_LENGTH} />
    </div>
  </div>
);

// ─── STEP 3: Clinical Findings ────────────────────────────────────────────────
const VitalInput = ({ label, value, onChange, placeholder }) => (
  <div className="flex items-center gap-1 sm:gap-2 bg-[#F5F6F8] rounded-xl px-2.5 sm:px-3 border border-transparent focus-within:bg-white focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 transition-all min-w-0">
    <span className="text-[10px] sm:text-xs text-gray-400 font-medium shrink-0 whitespace-nowrap">{label}</span>
    <div className="w-px h-5 bg-gray-200 shrink-0 mx-0.5 sm:mx-1" />
    <input
      type="number"
      step="any"
      min="0"
      className="flex-1 min-w-0 py-2.5 sm:py-3 text-xs sm:text-sm bg-transparent outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      value={value}
      onChange={(e) => {
        const val = e.target.value;
        if (val === '' || /^\d*\.?\d*$/.test(val)) {
          onChange(e);
        }
      }}
      placeholder={placeholder || '0'}
    />
  </div>
);

const VitalSelect = ({ label, value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative min-w-0" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center gap-1 sm:gap-2 bg-[#F5F6F8] rounded-xl px-2.5 sm:px-3 border transition-all text-left min-w-0 ${isOpen
            ? 'bg-white border-indigo-500 ring-2 ring-indigo-100 shadow-sm'
            : 'border-transparent hover:bg-[#eceef2]'
          }`}
      >
        <span className="text-[10px] sm:text-xs text-gray-400 font-medium shrink-0 whitespace-nowrap">
          {label}
        </span>
        <div className="w-px h-5 bg-gray-200 shrink-0 mx-0.5 sm:mx-1" />
        <span className="flex-1 min-w-0 py-2.5 sm:py-3 text-xs sm:text-sm text-gray-800 font-medium truncate">
          {value || '—'}
        </span>
        <ChevronDown
          size={14}
          className={`text-gray-500 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180 text-indigo-600' : ''
            }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-[0_12px_30px_-6px_rgba(0,0,0,0.14)] p-1 max-h-52 overflow-auto min-w-[120px]">
          {options.map((option) => {
            const isSelected = value === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-xs sm:text-sm transition-colors ${isSelected
                    ? 'bg-indigo-50 text-indigo-900 font-semibold'
                    : 'text-gray-800 hover:bg-[#F5F6F8] font-medium'
                  }`}
              >
                <span className="truncate">{option}</span>
                {isSelected && <Check size={14} className="text-indigo-600 shrink-0 ml-1" strokeWidth={2.5} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Step3 = ({ form, onChange, onPreview }) => (
  <div className="space-y-6">
    {/* Vitals Grid — 2 columns (4 rows) on all screen sizes */}
    <div>
      <label className={labelCls}>Vitals</label>
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <VitalInput label="Temp (°F)" value={form.vitalsTemp} onChange={(e) => onChange('vitalsTemp', e?.target?.value ?? e ?? '')} placeholder="102" />
        <VitalSelect label="BCS" value={form.vitalsBCS} onChange={(val) => onChange('vitalsBCS', typeof val === 'string' ? val : (val?.target?.value ?? ''))} options={BCS_OPTIONS} />
        <VitalInput label="Wt (kg)" value={form.vitalsWeight} onChange={(e) => onChange('vitalsWeight', e?.target?.value ?? e ?? '')} />
        <VitalSelect label="Pain" value={form.vitalsPain} onChange={(val) => onChange('vitalsPain', typeof val === 'string' ? val : (val?.target?.value ?? ''))} options={PAIN_OPTIONS} />
        <VitalInput label="HR (/min)" value={form.vitalsHR} onChange={(e) => onChange('vitalsHR', e?.target?.value ?? e ?? '')} />
        <VitalSelect label="CRT(s)" value={form.vitalsCRT} onChange={(val) => onChange('vitalsCRT', typeof val === 'string' ? val : (val?.target?.value ?? ''))} options={CRT_OPTIONS} />
        <VitalInput label="RR (/min)" value={form.vitalsRR} onChange={(e) => onChange('vitalsRR', e?.target?.value ?? e ?? '')} />
        <VitalSelect label="MM color" value={form.vitalsMM} onChange={(val) => onChange('vitalsMM', typeof val === 'string' ? val : (val?.target?.value ?? ''))} options={MM_OPTIONS} />
      </div>
    </div>

    {/* Physical Exam Findings (with inline media upload buttons on right) */}
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className={labelCls}>Physical Examination Findings</label>
        <InlineMediaUploader section="CLINICAL_FINDINGS" media={form.media} onChange={(m) => onChange('media', m)} />
      </div>
      <AutoTextarea
        rows={5}
        value={form.physicalExam}
        onChange={(e) => onChange('physicalExam', e.target.value)}
        placeholder="e.g. BAR. Cardiovascular: Grade III/VI systolic murmur at left apex. Abdomen: mild cranial discomfort on palpation. Lymph nodes within normal limits..."
        maxLength={MAX_FIELD_LENGTH}
      />
      <FieldCounter current={form.physicalExam.length} max={MAX_FIELD_LENGTH} />

      {/* Uploaded Physical Examination Findings Media Grid directly below text box */}
      <div className="mt-3">
        <SectionMediaGrid section="CLINICAL_FINDINGS" media={form.media} onChange={(m) => onChange('media', m)} onPreview={onPreview} />
      </div>
    </div>
  </div>
);

// ─── STEP 4: Assessment ───────────────────────────────────────────────────────
const Step4 = ({ form, onChange, onPreview }) => (
  <div className="space-y-5">
    {/* Differential Diagnosis */}
    <div>
      <label className={labelCls}>Differential Diagnosis <span className="text-red-500">*</span></label>
      <AutoTextarea
        rows={4}
        value={form.differentialDiagnosis}
        onChange={(e) => onChange('differentialDiagnosis', e.target.value)}
        placeholder={`e.g. 1. Chronic Kidney Disease (CKD) — most likely given azotaemia and weight loss\n2. Inflammatory Bowel Disease (IBD)\n3. Alimentary lymphoma\n4. Gastric foreign body`}
        maxLength={MAX_FIELD_LENGTH}
      />
      <FieldCounter current={form.differentialDiagnosis.length} max={MAX_FIELD_LENGTH} />
    </div>

    {/* Diagnostics Plan */}
    <div>
      <label className={labelCls}>Diagnostics Plan <span className="text-red-500">*</span></label>
      <AutoTextarea
        rows={4}
        value={form.diagnosticsPlan}
        onChange={(e) => onChange('diagnosticsPlan', e.target.value)}
        placeholder="e.g. CBC, comprehensive chemistry panel, urinalysis with UPC ratio, abdominal ultrasound, total T4..."
        maxLength={MAX_FIELD_LENGTH}
      />
      <FieldCounter current={form.diagnosticsPlan.length} max={MAX_FIELD_LENGTH} />
    </div>

    {/* Diagnostic Results (with inline media upload buttons on right) */}
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className={labelCls}>
          Diagnostic Results <span className={subLabelCls}>(optional)</span>
        </label>
        <InlineMediaUploader section="DIAGNOSTICS" media={form.media} onChange={(m) => onChange('media', m)} />
      </div>
      <AutoTextarea
        rows={4}
        value={form.diagnosticResults}
        onChange={(e) => onChange('diagnosticResults', e.target.value)}
        placeholder="e.g. CBC: mild non-regenerative anaemia (PCV 28%). Chemistry: BUN 45 mg/dL (elevated), Creatinine 2.1 mg/dL (elevated). Abdominal ultrasound: bilateral renomegaly with irregular cortical margins..."
        maxLength={MAX_FIELD_LENGTH}
      />
      <FieldCounter current={form.diagnosticResults.length} max={MAX_FIELD_LENGTH} />

      {/* Uploaded Diagnostic Results Media Grid directly below text box */}
      <div className="mt-3">
        <SectionMediaGrid section="DIAGNOSTICS" media={form.media} onChange={(m) => onChange('media', m)} onPreview={onPreview} />
      </div>
    </div>

    {/* Final Diagnosis */}
    <div>
      <label className={labelCls}>Final Diagnosis <span className="text-red-500">*</span></label>
      <AutoTextarea
        rows={4}
        value={form.finalDiagnosis}
        onChange={(e) => onChange('finalDiagnosis', e.target.value)}
        placeholder="e.g. Chronic Kidney Disease (CKD) Stage III with secondary non-regenerative anaemia..."
        maxLength={MAX_FIELD_LENGTH}
      />
      <FieldCounter current={form.finalDiagnosis.length} max={MAX_FIELD_LENGTH} />
    </div>

    {/* Prognosis */}
    <div>
      <label className={labelCls}>
        Prognosis <span className={subLabelCls}>(optional)</span>
      </label>
      <AutoTextarea
        rows={4}
        value={form.prognosis}
        onChange={(e) => onChange('prognosis', e.target.value)}
        placeholder="e.g. Guarded to fair with appropriate management. Disease progression can be slowed with dietary intervention and phosphate restriction..."
        maxLength={MAX_FIELD_LENGTH}
      />
      <FieldCounter current={form.prognosis.length} max={MAX_FIELD_LENGTH} />
    </div>
  </div>
);

const CASE_TYPE_OPTIONS = [
  'Surgical',
  'Diagnostic Challenge',
  'Emergency',
  'Clinical Essentials',
  'Pathology',
  'Dermatology',
  'General',
  'Ask and Learn'
];
const VISIBILITY_OPTIONS = ['Public', 'Private'];

const Step5 = ({ form, onChange }) => (
  <div className="space-y-6">
    {/* Treatment Plan */}
    <div>
      <label className={labelCls}>
        Treatment Plan <span className="text-red-500">*</span>
      </label>
      <AutoTextarea
        rows={5}
        value={form.outcome}
        onChange={(e) => onChange('outcome', e.target.value)}
        placeholder="e.g. IV fluid therapy (LRS 60 mL/kg/day), maropitant 1 mg/kg SQ SID, phosphate binder initiated, renal diet prescribed. Patient stabilised after 48-hour hospitalisation. Follow-up in 2 weeks for recheck bloodwork..."
        maxLength={MAX_FIELD_LENGTH}
      />
      <FieldCounter current={form.outcome.length} max={MAX_FIELD_LENGTH} />
    </div>

    {/* Horizontal Divider */}
    <div className="border-t border-gray-100" />

    {/* Case Type & Visibility (2-Column Grid) */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className={labelCls}>Case Type</label>
        <CustomSelect
          options={CASE_TYPE_OPTIONS}
          value={form.category || 'Surgical'}
          onChange={(val) => onChange('category', typeof val === 'string' ? val : (val?.target?.value ?? 'Surgical'))}
          placeholder="Select Case Type"
        />
      </div>
      <div>
        <label className={labelCls}>Visibility</label>
        <CustomSelect
          options={VISIBILITY_OPTIONS}
          value={form.visibility === 'Private' || form.isRestricted ? 'Private' : 'Public'}
          onChange={(val) => {
            const v = typeof val === 'string' ? val : (val?.target?.value ?? 'Public');
            onChange('visibility', v);
            onChange('isRestricted', v === 'Private');
          }}
          placeholder="Public"
        />
      </div>
    </div>

    {/* Horizontal Divider */}
    <div className="border-t border-gray-100" />
  </div>
);

// ─── Success Screen ───────────────────────────────────────────────────────────
const SuccessScreen = ({ onClose, editMode }) => (
  <div className="flex flex-col items-center justify-center py-12 px-8 text-center">
    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
      <Check size={32} className="text-green-600" />
    </div>
    <h2 className="text-xl font-bold text-gray-900 mb-2">
      {editMode ? 'Case Updated Successfully!' : 'Case Shared Successfully!'}
    </h2>
    <p className="text-gray-500 text-xs max-w-xs leading-relaxed mb-6">
      {editMode
        ? 'Your changes have been saved to the community feed.'
        : 'Your case has been published to the community. You can now track engagement and discussions on this case.'}
    </p>
    <button
      type="button"
      onClick={onClose}
      className="px-6 py-2.5 text-xs font-bold text-white bg-[#6C63FF] hover:bg-[#5850E6] rounded-xl transition-colors shadow-sm"
    >
      Done
    </button>
  </div>
);

// ─── Landing Screen ───────────────────────────────────────────────────────────
const LandingScreen = ({ onSelectQuickDiscussion, onSelectFullCase, onClose }) => (
  <div className="flex flex-col px-8 pt-6 pb-8">
    <button
      type="button"
      onClick={onClose}
      className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-900 mb-6 self-start transition-colors"
    >
      <ArrowLeft size={16} /> Back
    </button>

    <h1 className="text-2xl font-bold text-gray-900 mb-1">Share a Case</h1>
    <p className="text-gray-400 text-xs mb-6">Choose how you'd like to share with the Vet Collab community.</p>

    <div className="space-y-3">
      {/* Quick Discussion option */}
      <button
        type="button"
        onClick={onSelectQuickDiscussion}
        className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl hover:border-indigo-400 hover:shadow-sm transition-all group text-left"
      >
        <div className="flex items-center gap-3.5">
          <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center shrink-0">
            <Zap size={20} className="text-purple-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Quick Discussion</p>
            <p className="text-xs text-gray-400 mt-0.5">Share clinical observations, news, research papers, questions or insights.</p>
          </div>
        </div>
        <ChevronRight size={18} className="text-gray-400 group-hover:text-purple-500 transition-colors shrink-0" />
      </button>

      {/* Full Case option */}
      <button
        type="button"
        onClick={onSelectFullCase}
        className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl hover:border-indigo-400 hover:shadow-sm transition-all group text-left"
      >
        <div className="flex items-center gap-3.5">
          <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center shrink-0">
            <FileText size={20} className="text-indigo-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Full Case</p>
            <p className="text-xs text-gray-400 mt-0.5">Share a complete clinical case with patient info, vitals, diagnostics, and treatment.</p>
          </div>
        </div>
        <ChevronRight size={18} className="text-gray-400 group-hover:text-indigo-500 transition-colors shrink-0" />
      </button>
    </div>
  </div>
);


// ─── Full Case Preview Component (Viewer Mode Presentation) ──────────────────
const FullCasePreview = ({ form, onEdit, onSubmit, isPending, error, user }) => {
  const speciesText = form.species === 'Other' ? (form.customSpecies || 'Other') : form.species;
  const breedText = form.breed;
  const age = form.ageValue ? `${form.ageValue} ${form.ageUnit}` : '';
  const formattedPatient = [
    speciesText,
    breedText,
    age,
    form.sex,
    form.sterilization
  ].filter(Boolean).join(' · ');

  const historyMedia = (form.media || []).filter(m => m.section === 'CHIEF_COMPLAINT');
  const findingsMedia = (form.media || []).filter(m => m.section === 'CLINICAL_FINDINGS');
  const diagnosticsMedia = (form.media || []).filter(m => m.section === 'DIAGNOSTICS');

  return (
    <div className="w-full max-w-4xl mx-auto py-4 px-3 sm:px-6 pb-28">
      {/* Top Bar Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={onEdit}
          className="flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft size={16} /> Edit Case
        </button>

        <div className="flex items-center gap-2">
          <span className="px-3 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-semibold rounded-full">
            {form.category || 'Surgical'}
          </span>
          <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${form.visibility === 'Private' || form.isRestricted
              ? 'bg-amber-50 text-amber-700 border-amber-200'
              : 'bg-emerald-50 text-emerald-700 border-emerald-200'
            }`}>
            {form.visibility === 'Private' || form.isRestricted ? 'Private' : 'Public'}
          </span>
          <span className="px-3 py-1 bg-purple-50 text-purple-700 border border-purple-200 text-xs font-semibold rounded-full">
            Preview Mode
          </span>
        </div>
      </div>

      {/* Main Published Case Card (Matches CaseDetailPage viewer layout) */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8 space-y-8">
        {/* Header / Author */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-lg overflow-hidden border border-indigo-200">
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              (user?.name || 'U').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-gray-900 text-base">Dr. {user?.name || 'Practitioner'}</h3>
              <span className="bg-indigo-50 text-indigo-700 px-3 py-0.5 rounded-full text-xs font-semibold border border-indigo-100">
                {user?.jobRole || user?.specialization || user?.specialty || (user?.role === 'student' ? 'Student' : 'Veterinarian')}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
              <span>Just now</span>
              <span>•</span>
              {(breedText || speciesText) && (
                <span className="bg-pink-50 text-pink-700 px-2.5 py-0.5 rounded-full font-medium text-xs border border-pink-100">
                  {breedText || speciesText}
                </span>
              )}
              <span className="bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded-full font-medium text-xs border border-emerald-100">
                {form.category || 'Clinical Essentials'}
              </span>
            </div>
          </div>
        </div>

        {/* Case Title */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{form.title || 'Untitled Case'}</h1>
        </div>

        {/* Visit Details / Patient Info */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Stethoscope className="text-blue-500" size={18} />
            <h3 className="text-sm font-semibold text-gray-900">Patient Info</h3>
          </div>
          {formattedPatient && (
            <div className="text-sm text-gray-700 mb-2 font-medium">
              {formattedPatient}
            </div>
          )}
          {form.chiefComplaint && (
            <div className="text-sm text-gray-700">
              <span className="font-semibold">Chief Complaint: </span>
              {form.chiefComplaint}
            </div>
          )}
          {form.history && (
            <div className="text-sm text-gray-700 mt-1">
              <span className="font-semibold">History: </span>
              {form.history}
            </div>
          )}
          {historyMedia.length > 0 && <PreviewMediaGallery items={historyMedia} />}
        </div>

        {/* Clinical Findings & Vitals */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Activity className="text-emerald-500" size={18} />
            <h3 className="text-sm font-semibold text-gray-900">Clinical Findings &amp; Vitals</h3>
          </div>

          {/* Vitals Grid */}
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Vitals</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center">
                <HeartPulse size={16} className="text-red-500 mb-1" />
                <span className="text-xs text-gray-500 font-medium">Heart Rate</span>
                <span className="text-sm font-semibold text-gray-900">{form.vitalsHR ? `${form.vitalsHR} /min` : 'N/A'}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center">
                <Wind size={16} className="text-blue-400 mb-1" />
                <span className="text-xs text-gray-500 font-medium">Resp Rate</span>
                <span className="text-sm font-semibold text-gray-900">{form.vitalsRR ? `${form.vitalsRR} /min` : 'N/A'}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center">
                <Thermometer size={16} className="text-red-400 mb-1" />
                <span className="text-xs text-gray-500 font-medium">Temperature</span>
                <span className="text-sm font-semibold text-gray-900">{form.vitalsTemp ? `${form.vitalsTemp}°F` : 'N/A'}</span>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center">
                <Scale size={16} className="text-purple-400 mb-1" />
                <span className="text-xs text-gray-500 font-medium">Weight</span>
                <span className="text-sm font-semibold text-gray-900">{form.vitalsWeight ? `${form.vitalsWeight} kg` : 'N/A'}</span>
              </div>
              {form.vitalsBCS && form.vitalsBCS !== '—' && (
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center">
                  <Activity size={16} className="text-indigo-400 mb-1" />
                  <span className="text-xs text-gray-500 font-medium">BCS</span>
                  <span className="text-sm font-semibold text-gray-900">{form.vitalsBCS}</span>
                </div>
              )}
              {form.vitalsPain && form.vitalsPain !== '—' && (
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center">
                  <AlertCircle size={16} className="text-orange-400 mb-1" />
                  <span className="text-xs text-gray-500 font-medium">Pain Score</span>
                  <span className="text-sm font-semibold text-gray-900">{form.vitalsPain}</span>
                </div>
              )}
              {form.vitalsCRT && form.vitalsCRT !== '—' && (
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center">
                  <Activity size={16} className="text-teal-400 mb-1" />
                  <span className="text-xs text-gray-500 font-medium">CRT</span>
                  <span className="text-sm font-semibold text-gray-900">{form.vitalsCRT}</span>
                </div>
              )}
              {form.vitalsMM && form.vitalsMM !== '—' && (
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center">
                  <Heart size={16} className="text-pink-400 mb-1" />
                  <span className="text-xs text-gray-500 font-medium">MM Color</span>
                  <span className="text-sm font-semibold text-gray-900">{form.vitalsMM}</span>
                </div>
              )}
            </div>
          </div>

          {/* Physical Examination Findings */}
          {form.physicalExam && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Physical Examination Findings</h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {form.physicalExam}
              </p>
            </div>
          )}

          {/* Clinical Findings Media */}
          {findingsMedia.length > 0 && <PreviewMediaGallery items={findingsMedia} />}
        </div>

        {/* Diagnostics & Assessment */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <FlaskConical className="text-indigo-500" size={18} />
            <h3 className="text-sm font-semibold text-gray-900">Diagnostics &amp; Assessment</h3>
          </div>
          <div className="space-y-3 text-sm text-gray-700">
            {form.differentialDiagnosis && (
              <div>
                <span className="font-semibold block text-gray-900">Differential Diagnosis:</span>
                <p className="whitespace-pre-wrap leading-relaxed">{form.differentialDiagnosis}</p>
              </div>
            )}
            {form.diagnosticsPlan && (
              <div>
                <span className="font-semibold block text-gray-900">Diagnostics Plan:</span>
                <p className="whitespace-pre-wrap leading-relaxed">{form.diagnosticsPlan}</p>
              </div>
            )}
            {form.diagnosticResults && (
              <div>
                <span className="font-semibold block text-gray-900">Diagnostic Results:</span>
                <p className="whitespace-pre-wrap leading-relaxed">{form.diagnosticResults}</p>
              </div>
            )}
            {diagnosticsMedia.length > 0 && <PreviewMediaGallery items={diagnosticsMedia} />}
            {form.finalDiagnosis && (
              <div>
                <span className="font-semibold block text-gray-900">Final Diagnosis:</span>
                <p className="whitespace-pre-wrap leading-relaxed">{form.finalDiagnosis}</p>
              </div>
            )}
            {form.prognosis && (
              <div>
                <span className="font-semibold block text-gray-900">Prognosis:</span>
                <p className="whitespace-pre-wrap leading-relaxed">{form.prognosis}</p>
              </div>
            )}
          </div>
        </div>

        {/* Treatment Plan */}
        {form.outcome && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Stethoscope className="text-emerald-500" size={18} />
              <h3 className="text-sm font-semibold text-gray-900">Treatment Plan</h3>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
              {form.outcome}
            </p>
          </div>
        )}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 shrink-0 shadow-sm font-medium">
          <AlertCircle size={16} className="shrink-0" /> {error}
        </div>
      )}

      {/* Bottom Action Bar — Natural document flow below main card (Screenshot 2) */}
      <div className="mt-6 w-full bg-white rounded-2xl border border-gray-200 p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs sm:text-sm font-medium text-gray-600">
          Looks good? Publish to share with the community.
        </span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onSubmit}
            disabled={isPending}
            className="px-6 py-2.5 text-xs sm:text-sm font-bold text-white bg-[#6C63FF] hover:bg-[#5850E6] rounded-xl transition-colors disabled:opacity-60 flex items-center gap-2 shadow-md"
          >
            {isPending ? <><Loader2 size={15} className="animate-spin" /> Publishing...</> : '✓ Publish Case'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Initial Form State ───────────────────────────────────────────────────────
const INITIAL = {
  title: '', species: '', breed: '',
  ageValue: '', ageUnit: 'years',
  weightValue: '', weightUnit: 'kg',
  sex: '', sterilization: 'Intact',
  chiefComplaint: '', history: '',
  vitalsTemp: '', vitalsBCS: '—', vitalsWeight: '',
  vitalsPain: '0 – None', vitalsHR: '', vitalsCRT: '—',
  vitalsRR: '', vitalsMM: '—',
  physicalExam: '',
  differentialDiagnosis: '', diagnosticsPlan: '', diagnosticResults: '',
  finalDiagnosis: '', prognosis: '',
  plan: '', outcome: '', keyLearningsText: '',
  media: [],
};

// ─── Main Wizard ──────────────────────────────────────────────────────────────
const ShareCaseWizard = ({ isOpen, onClose, editMode = false, caseId = null, initialData = null }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const draftKey = !editMode && user ? `share_case_${user.id || user._id || 'guest'}` : 'share_case_draft';

  const [view, setView] = useState(() => {
    if (editMode) {
      if (initialData?.postType === 'quick_discussion') return 'quick_discussion';
      return 'form';
    }
    const saved = draftStore.loadDraft(draftKey);
    return saved?.view || 'landing';
  });

  const [step, setStep] = useState(() => {
    if (editMode) return 1;
    const saved = draftStore.loadDraft(draftKey);
    return saved?.step || 1;
  });

  const [form, setForm] = useState(() => {
    if (editMode && initialData) {
      const media = (initialData.media || []).map((m) => ({
        // The KEY, not the signed URL the API returns alongside it. `url` is
        // what gets submitted back on save, and a signed URL would be stored
        // verbatim — the case's media would then break when that signature
        // expired a few hours later. `mediaKey` is the raw stored value.
        url: m.mediaKey ?? m.url,
        // Display only, and stripped from the payload before saving.
        previewUrl: formatMediaUrl(m.url),
        filename: m.filename,
        type: m.type,
        mimeType: m.mimeType || null,
        sizeBytes: m.sizeBytes || null,
        section: m.section,
        order: m.order || 0,
      }));
      return {
        ...INITIAL,
        title: initialData.title || '',
        species: initialData.species || initialData.patientInfo?.match(/Dog|Cat|Other/)?.[0] || '',
        breed: initialData.breed || '',
        ageValue: initialData.ageValue || '',
        ageUnit: initialData.ageUnit || 'years',
        sex: initialData.sex || '',
        sterilization: initialData.sterilization || 'Intact',
        chiefComplaint: initialData.chiefComplaint || '',
        history: initialData.reasonForVisit || '',
        vitalsTemp: initialData.vitalsTemp || '',
        vitalsHR: initialData.vitalsHeartRate || '',
        vitalsRR: initialData.vitalsRespRate || '',
        vitalsWeight: initialData.vitalsWeight || '',
        vitalsBCS: initialData.vitalsBCS || '—',
        vitalsPain: initialData.vitalsPain || '0 – None',
        vitalsCRT: initialData.vitalsCRT || '—',
        vitalsMM: initialData.vitalsMM || '—',
        physicalExam: initialData.physicalExam || '',
        differentialDiagnosis: initialData.differentialDiagnosis || initialData.findings || '',
        diagnosticsPlan: initialData.diagnosticsPlan || initialData.assessment || '',
        diagnosticResults: initialData.diagnosticResults || '',
        finalDiagnosis: initialData.finalDiagnosis || '',
        prognosis: initialData.prognosis || '',
        plan: initialData.plan || initialData.outcome || '',
        outcome: initialData.outcome || '',
        keyLearningsText: (initialData.keyLearnings || []).join('\n'),
        media,
      };
    }
    if (!editMode && draftKey) {
      const saved = draftStore.loadDraft(draftKey);
      // Sanitise on the way in as well as out: drafts written before this
      // existed still carry dead blob URLs and `uploading: true` items that
      // nothing can clear, which blocks Publish for good.
      if (saved?.form) return { ...saved.form, media: draftSafeMedia(saved.form.media) };
    }
    return INITIAL;
  });

  const [error, setError] = useState('');
  // The key of the item open in the lightbox, never a copy of the item itself.
  // A staged file's thumbnail is fetched after the form renders, so a snapshot
  // taken on click would keep the version that had no preview and show a broken
  // image even once the real one had arrived.
  const [previewKey, setPreviewKey] = useState(null);
  const previewMedia = previewKey
    ? (form.media || []).find((m) => m.url === previewKey) ?? null
    : null;

  // Check for unsaved draft on modal open or reset view in editMode
  useEffect(() => {
    if (isOpen && editMode && initialData) {
      if (initialData.postType === 'quick_discussion') {
        setView('quick_discussion');
      } else {
        setView('form');
      }
    } else if (isOpen && !editMode && draftKey) {
      const saved = draftStore.loadDraft(draftKey);
      if (saved?.form && (saved.form.title || saved.form.chiefComplaint || saved.form.species)) {
        if (saved.view) setView(saved.view);
        if (saved.step) setStep(saved.step);
        if (saved.form) setForm({ ...saved.form, media: draftSafeMedia(saved.form.media) });
      }
    }
  }, [isOpen, editMode, draftKey, initialData]);

  // Auto-save draft when form, step, or view changes
  useEffect(() => {
    if (!isOpen || editMode || !draftKey) return;
    const isDirty = form.title || form.chiefComplaint || form.species || form.differentialDiagnosis || form.outcome;
    if (!isDirty && step === 1 && view === 'landing') return;

    const timer = setTimeout(() => {
      draftStore.saveDraft(draftKey, {
        view,
        step,
        form: { ...form, media: draftSafeMedia(form.media) },
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [isOpen, editMode, draftKey, view, step, form]);

  /**
   * Restore thumbnails for staged files after a draft is reopened.
   *
   * The draft keeps each file's storage key but not its `blob:` preview — that
   * belonged to the page that created it. Without this the tile can only say
   * "Attached", which reads like the upload was lost even though the bytes are
   * in the bucket.
   *
   * Only staged (`tmp/`) keys need it. Media loaded from the API in edit mode
   * already arrives signed.
   */
  // Depend on the list of keys still needing a preview, not on `form.media`.
  //
  // Writing a preview into form.media changes that array, which would re-run
  // this effect and cancel the run that is still fetching the others — so only
  // the first file ever got a thumbnail and the rest kept the placeholder. This
  // string only changes when the set of outstanding keys does.
  const stagedKeysNeedingPreview = (form.media || [])
    .filter((m) => !m.previewUrl && typeof m.url === 'string' && m.url.startsWith('tmp/'))
    .map((m) => m.url)
    .join('|');

  useEffect(() => {
    const pending = stagedKeysNeedingPreview ? stagedKeysNeedingPreview.split('|') : [];
    if (pending.length === 0) return;

    // No "already requested" set here, deliberately. StrictMode runs effects
    // setup → cleanup → setup on the same instance, so a ref survives between
    // them: the first setup marked every key and had its results thrown away by
    // its own cleanup, and the second setup then saw nothing left to do. The
    // result was that no preview was ever applied.
    //
    // The dependency is the guard instead. On success these keys gain a
    // previewUrl and drop out of the list, so the effect settles. On failure the
    // list is unchanged, so it does not re-run and cannot loop.
    let active = true;
    (async () => {
      // All at once, then one patch. Patching per file would change form.media
      // between iterations and cancel the run still fetching the rest.
      const settled = await Promise.all(
        pending.map(async (key) => {
          try {
            const result = await presignStagedPreview(key);
            return result?.url ? [key, result.url] : null;
          } catch {
            // The file is still staged and will publish; only its thumbnail is
            // missing, which is not worth an error banner.
            return null;
          }
        })
      );
      if (!active) return;

      const found = new Map(settled.filter(Boolean));
      if (found.size === 0) return;

      setForm((prev) => ({
        ...prev,
        media: (prev.media || []).map((m) =>
          found.has(m.url) ? { ...m, previewUrl: found.get(m.url) } : m
        ),
      }));
    })();

    return () => {
      active = false;
    };
  }, [stagedKeysNeedingPreview]);

  const { mutate: createCase, isPending: isCreating } = useCreateCase();
  const { mutate: updateCase, isPending: isUpdating } = useUpdateCase();
  // Media uploads run while the form is being filled in, so Publish only waits
  // on the save itself.
  const isPending = isCreating || isUpdating;

  const onChange = (field, value) => {
    // Accepts an updater function as well as a value. Media uploads run in the
    // background and report progress after the fact, so they must patch the
    // list as it is at that moment rather than a copy captured when the upload
    // started — otherwise two concurrent uploads overwrite each other.
    setForm((prev) => ({
      ...prev,
      [field]: typeof value === 'function' ? value(prev[field]) : value,
    }));
    setError('');
  };

  const validate = () => {
    if (step === 1) {
      if (!form.title.trim()) { setError('Case title is required.'); return false; }
      if (!form.species) { setError('Please select a species.'); return false; }
      if (!String(form.ageValue || '').trim()) { setError('Age is required.'); return false; }
      if (!form.sex) { setError('Please select sex.'); return false; }
    }
    if (step === 2) {
      if (!form.chiefComplaint.trim()) { setError('Chief complaint is required.'); return false; }
    }
    if (step === 4) {
      if (!form.differentialDiagnosis.trim()) { setError('Differential diagnosis is required.'); return false; }
      if (!form.diagnosticsPlan.trim()) { setError('Diagnostics plan is required.'); return false; }
      if (!form.finalDiagnosis.trim()) { setError('Final diagnosis is required.'); return false; }
    }
    if (step === 5) {
      if (!form.outcome.trim()) { setError('Treatment plan is required.'); return false; }
    }
    return true;
  };

  // Files still transferring, anywhere in the form. Same condition the Publish
  // guard uses, so the wizard blocks at the step where the upload was started
  // rather than only at the very end.
  const pendingUploads = (form.media || []).filter((m) => m.uploading);
  const isUploading = pendingUploads.length > 0;
  const uploadProgress = isUploading
    ? Math.min(...pendingUploads.map((m) => m.progress ?? 0))
    : 100;

  const handleNext = () => {
    // Also guarded here, not only on the button: the button can be reached by
    // keyboard, and a step can be advanced while a transfer is in flight.
    if (isUploading) {
      setError(
        pendingUploads.length === 1
          ? `Still uploading 1 file — ${uploadProgress}% done.`
          : `Still uploading ${pendingUploads.length} files — ${uploadProgress}% done.`
      );
      return;
    }
    if (!validate()) return;
    setError('');
    if (step === 5) {
      setView('preview');
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length));
  };

  const handleBack = () => {
    setError('');
    if (step === 1) { setView('landing'); setStep(1); return; }
    setStep((s) => Math.max(s - 1, 1));
  };

  const handleClose = () => {
    setView('landing');
    setStep(1);
    setForm(INITIAL);
    setError('');
    if (!editMode && draftKey) draftStore.clearDraft(draftKey);
    onClose();
  };

  const validateAll = () => {
    if (!form.title.trim()) { setError('Case title is required.'); return false; }
    if (!form.species) { setError('Please select a species.'); return false; }
    if (!form.ageValue || !String(form.ageValue).trim()) { setError('Age is required.'); return false; }
    if (!form.sex) { setError('Please select sex.'); return false; }
    if (!form.chiefComplaint.trim()) { setError('Chief complaint is required.'); return false; }
    if (!form.differentialDiagnosis.trim()) { setError('Differential diagnosis is required.'); return false; }
    if (!form.diagnosticsPlan.trim()) { setError('Diagnostics plan is required.'); return false; }
    if (!form.finalDiagnosis.trim()) { setError('Final diagnosis is required.'); return false; }
    if (!form.outcome.trim()) { setError('Treatment plan is required.'); return false; }
    return true;
  };

  const handleSubmit = async () => {
    // Validate before uploading, so an invalid form costs no stored object.
    if (!validateAll()) return;

    // Media uploaded to B2 as soon as it was picked, so by now each item should
    // carry a staging key. Anything still in flight has no key to submit yet,
    // and anything that failed would be saved as a broken reference.
    const media = form.media || [];
    const pending = media.filter((m) => m.uploading);
    if (pending.length > 0) {
      // Say what is actually being waited on. A large video on a slow
      // connection can take minutes, and "please wait" with no number reads
      // like the form is stuck.
      const slowest = Math.min(...pending.map((m) => m.progress ?? 0));
      setError(
        pending.length === 1
          ? `Still uploading 1 file — ${slowest}% done. Publish will be ready when it finishes.`
          : `Still uploading ${pending.length} files — ${slowest}% done. Publish will be ready when they finish.`
      );
      return;
    }
    if (media.some((m) => m.failed)) {
      setError('Some media failed to upload. Remove those items and try again.');
      return;
    }

    const age = form.ageValue
      ? `${form.ageValue} ${form.ageUnit}`
      : '';
    const weight = form.weightValue
      ? `${form.weightValue} ${form.weightUnit}`
      : '';
    const patientInfo = [form.species, form.breed, age, form.sex, form.sterilization]
      .filter(Boolean).join(', ');

    const keyLearnings = form.keyLearningsText
      ? form.keyLearningsText.split('\n').map((l) => l.trim()).filter(Boolean)
      : [];

    const payload = {
      title: form.title,
      content: form.chiefComplaint.trim() || form.title,
      patientInfo,
      species: form.species,
      breed: form.breed,
      ageValue: form.ageValue,
      ageUnit: form.ageUnit,
      sex: form.sex,
      sterilization: form.sterilization,
      chiefComplaint: form.chiefComplaint,
      reasonForVisit: form.history,
      vitalsTemp: form.vitalsTemp,
      vitalsHeartRate: form.vitalsHR,
      vitalsRespRate: form.vitalsRR,
      vitalsWeight: form.vitalsWeight || weight,
      vitalsBCS: form.vitalsBCS,
      vitalsPain: form.vitalsPain,
      vitalsCRT: form.vitalsCRT,
      vitalsMM: form.vitalsMM,
      physicalExam: form.physicalExam,
      differentialDiagnosis: form.differentialDiagnosis,
      diagnosticsPlan: form.diagnosticsPlan,
      findings: form.differentialDiagnosis,
      assessment: form.diagnosticsPlan,
      diagnosticResults: form.diagnosticResults,
      finalDiagnosis: form.finalDiagnosis,
      prognosis: form.prognosis,
      plan: form.outcome,
      outcome: form.outcome,
      causeOfConcern: '',
      tags: [],
      category: form.category || 'Surgical',
      isRestricted: form.visibility === 'Private' || !!form.isRestricted,
      keyLearnings,
      // `media` here is the post-upload list: every url is a storage key. The
      // local `file` handle is deliberately not spread into the payload.
      media: media.map((m, idx) => ({
        section: m.section,
        type: m.type,
        url: m.url,
        filename: m.filename,
        mimeType: m.mimeType || null,
        sizeBytes: m.sizeBytes || null,
        order: idx,
      })),
    };

    const onSuccess = async (res) => {
      if (!editMode && draftKey) draftStore.clearDraft(draftKey);
      setView('success');
    };

    const onError = (err) => {
      setError(err?.response?.data?.message || 'Failed to save case.');
    };

    if (editMode && caseId) {
      updateCase({ caseId, ...payload }, { onSuccess, onError });
    } else {
      createCase(payload, { onSuccess, onError });
    }
  };

  if (!isOpen) return null;

  const stepTitle = STEPS[step - 1]?.label;

  return (
    <div className={`fixed inset-0 z-50 bg-[#EAF0F8] overflow-y-auto flex justify-center ${view === 'preview' || view === 'quick_discussion' ? 'items-start pt-6 pb-28 px-3 sm:px-6' : 'items-center p-4 sm:p-6'
      }`}>
      {/* ── LANDING SCREEN ── */}
      {view === 'landing' && (
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-auto my-auto">
          <LandingScreen
            onSelectQuickDiscussion={() => setView('quick_discussion')}
            onSelectFullCase={() => { setView('form'); setStep(1); }}
            onClose={handleClose}
          />
        </div>
      )}

      {/* ── QUICK DISCUSSION COMPOSER ── */}
      {view === 'quick_discussion' && (
        <QuickDiscussionComposer
          onBack={editMode ? handleClose : () => setView('landing')}
          onClose={handleClose}
          onSuccess={
            editMode
              ? () => {
                  queryClient.invalidateQueries(['case', caseId]);
                  queryClient.invalidateQueries(['cases']);
                  handleClose();
                }
              : () => setView('success')
          }
          editMode={editMode}
          caseId={caseId}
          initialData={editMode ? initialData : null}
        />
      )}

      {/* ── PREVIEW SCREEN ── */}
      {view === 'preview' && (
        <FullCasePreview
          form={form}
          onEdit={() => { setView('form'); setStep(5); }}
          onSubmit={handleSubmit}
          isPending={isPending}
          error={error}
          user={user}
        />
      )}

      {/* ── FORM SCREEN ── */}
      {view === 'form' && (
        <div className="relative w-full max-w-3xl mx-auto flex flex-col items-start my-auto w-full">
          {/* Top Back Link Outside Card */}
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-900 mb-2 sm:mb-3 transition-colors ml-1 shrink-0"
          >
            <ArrowLeft size={16} /> Back
          </button>

          {/* Main White Card Container */}
          <div className="relative bg-white w-full flex flex-col
            p-4 sm:p-6 lg:p-8
            rounded-2xl sm:rounded-3xl
            shadow-lg sm:shadow-xl
            border border-gray-100/80">



            {/* Horizontal Stepper Bar */}
            <div className="shrink-0">
              <HorizontalStepper current={step} />
            </div>

            {/* Step Title Header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-2 sm:gap-4 shrink-0">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{stepTitle}</h2>
                <p className="text-xs text-gray-400 font-medium mt-0.5">Step {step} of {STEPS.length}</p>
              </div>
            </div>

            {/* Form Body */}
            <div className="pb-2">
              {step === 1 && <Step1 form={form} onChange={onChange} />}
              {step === 2 && <Step2 form={form} onChange={onChange} />}
              {step === 3 && <Step3 form={form} onChange={onChange} onPreview={(m) => setPreviewKey(m?.url ?? null)} />}
              {step === 4 && <Step4 form={form} onChange={onChange} onPreview={(m) => setPreviewKey(m?.url ?? null)} />}
              {step === 5 && <Step5 form={form} onChange={onChange} />}
            </div>

            {/* Error */}
            {error && (
              <div className="mt-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 shrink-0">
                <AlertCircle size={14} /> {error}
              </div>
            )}

            {/* Footer Buttons */}
            <div className="flex items-center justify-between pt-3 sm:pt-4 mt-3 sm:mt-4 border-t border-gray-100/80 bg-white shrink-0">
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center gap-1 sm:gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors"
              >
                {step === 1 ? 'Cancel' : <><ChevronLeft size={16} /> Back</>}
              </button>

              <button
                type="button"
                onClick={handleNext}
                disabled={isUploading}
                title={isUploading ? 'Waiting for the media upload to finish' : undefined}
                className="flex items-center gap-2 px-6 sm:px-8 py-2.5 sm:py-3 text-sm font-semibold text-white bg-[#6C63FF] hover:bg-[#5850E6] rounded-xl transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-[#6C63FF]"
              >
                {isUploading ? (
                  <>
                    <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    Uploading {uploadProgress}%
                  </>
                ) : (
                  <>
                    {step === 5 ? 'Preview Case' : 'Continue'} <ChevronRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SUCCESS SCREEN ── */}
      {view === 'success' && (
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-auto my-auto">
          <SuccessScreen
            onClose={handleClose}
            editMode={editMode}
          />
        </div>
      )}

      {/* ── FULL SCREEN MEDIA LIGHTBOX / PREVIEW MODAL ── */}
      <MediaPreviewModal
        mediaItem={previewMedia}
        onClose={() => setPreviewKey(null)}
      />
    </div>
  );
};

export default ShareCaseWizard;
